import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";

export type MicrophoneStatus = "idle" | "granted" | "denied" | "error";

export interface MicrophoneState {
    status: MicrophoneStatus;
    error: string | null;
}

const initialState: MicrophoneState = {
    status: "idle",
    error: null,
};

const microphoneSlice = createSlice({
    name: "microphone",
    initialState,
    reducers: {
        setMicrophoneStatus: (state, action: PayloadAction<MicrophoneStatus>) => {
            state.status = action.payload;
        },
        setMicrophoneError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
            if (state.status !== "denied") {
                state.status = "error";
            }
        },
        resetMicrophoneState: (state) => {
            state.status = "idle";
            state.error = null;
        },
    },
});

export const checkAndRequestMicrophonePermission = () => async (dispatch: AppDispatch) => {
    if (!navigator.mediaDevices?.getUserMedia) {
        dispatch(setMicrophoneError("getUserMedia not supported in this browser."));
        return "error";
    }

    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        dispatch(setMicrophoneStatus("granted"));
        return "granted";
    } catch (error: any) {
        if (error.name === "NotAllowedError") {
            dispatch(setMicrophoneStatus("denied"));
            dispatch(setMicrophoneError("Microphone access denied by user."));
            return "denied";
        } else {
            dispatch(setMicrophoneStatus("error"));
            dispatch(setMicrophoneError(`Microphone access error: ${error.message}`));
            return "error";
        }
    }
};

export const { setMicrophoneStatus, setMicrophoneError, resetMicrophoneState } = microphoneSlice.actions;

export default microphoneSlice.reducer;
